'use client';

import { analytics } from 'lib/analytics/analytics';
import { useTranslations } from 'next-intl';
import {
  heroDiscountCodes,
  getHeroDiscountPercentage,
} from 'components/shared/Cart/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import {
  MiscRingCheckoutTypes,
  RingChargerType,
  RingSizes,
} from 'content/products/ring';
import { UhxShopifyMap } from 'content/products/uhx';
import { ringReferralCodes } from 'content/referral';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { useForwardSearchParamsShopify } from 'hooks/navigation/useForwardSearchParamsShopify';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
import {
  addonsReducer,
  getCheckoutLineItems,
  getShippingEstimate,
  initialAddonsState,
  initialRareSelectionContext,
} from './utils/checkout';
import { highlightElement } from '../ring-air/utils/highlightElement';
import { addonsType } from '../ring-air/utils/types';
import { RingContextType, ShippingEstimateObject } from './utils/types';
import { Currency } from 'utils/Currency';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import {
  RareColors,
  RarePriceData,
  getRareDiscountConfig,
  productMap_rare,
} from 'content/products/rare';
import { getRareAddonPricing } from 'content/products/rare/addonPricing';
import { getTravelChargerPrice } from 'content/products/accessories';

export { RareColors };

export const RareSelectionContext = createContext<RingContextType>(
  initialRareSelectionContext,
);

export const RareSelectionContextProvider: React.FC<{
  children?: React.ReactNode;
  currency: Currency;
  defaultColor: RareColors;
  defaultSize: RingSizes | null;
  defaultUhx: boolean;
  directCheckout?: boolean;
  misc?: MiscRingCheckoutTypes;
  rareShippingEstimate?: {
    message: string;
    timeframe: string;
    subtitle: string;
  };
  estimates?: ShippingEstimateObject;
  baseStore?: ShopifyStore;
  notServicable?: boolean;
  notServicableMessage?: string;
}> = ({
  children,
  currency,
  defaultColor,
  defaultSize,
  defaultUhx,
  directCheckout,
  misc,
  rareShippingEstimate,
  estimates,
  baseStore,
  notServicable,
  notServicableMessage,
}) => {
  const t = useTranslations('ring');
  const router = useRouter();
  const searchParams = useSearchParams();
  const { workAffiliateDiscount, store, addToCart, affiliateDetails } =
    useCartStore(
      useShallow((s) => ({
        workAffiliateDiscount: s.workAffiliateDiscount,
        store: s.store,
        addToCart: s.addToCart,
        affiliateDetails: s.affiliateDetails,
      })),
    );

  // Use baseStore for product variant lookups if available (e.g., SA uses EU variants)
  // Keep using store for pricing/currency (e.g., SA uses SAR prices)
  const variantStore = baseStore || store;

  const showSizeRef = useRef<HTMLDivElement>(null);
  const showChargerRef = useRef<HTMLDivElement>(null);
  const coverageRef = useRef<HTMLDivElement>(null);
  const powerplugRef = useRef<HTMLDivElement>(null);
  const cartSummaryRef = useRef<HTMLDivElement>(null);

  const sectionRefs = {
    showSizeRef,
    showChargerRef,
    coverageRef,
    powerplugRef,
    cartSummaryRef,
  };

  const [campaignActive] = useState(false);
  const colorParam = searchParams?.get('color');
  const [color, setColor] = useState<RareColors>(
    colorParam && (Object.values(RareColors) as string[]).includes(colorParam)
      ? (colorParam as RareColors)
      : defaultColor,
  );
  const [size, setSize] = useState<RingSizes | null>(
    defaultSize ?? RingSizes.OPEN,
  );
  const [chargerType, setChargerType] = useState<RingChargerType>(
    RingChargerType.OG,
  );
  const [uhx, setUhx] = useState<boolean>(defaultUhx);
  const [isSizingKitSelected, setIsSizingKitSelected] = useState<boolean>(true);

  const [addonsState, dispatchAddons] = useReducer(
    addonsReducer,
    initialAddonsState,
  );
  const [isLoading, setIsLoading] = useState(false);

  const { afibCart, cardioAdaptibilityCart, cnoProCart, uhxCart } = addonsState;

  const setUhxCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_UHX_CART', payload: value });
  const setAfibCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_AFIB_CART', payload: value });
  const setCardioAdaptabilityCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CARDIO_ADAPTIBILITY_CART', payload: value });
  const setCnoProCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CNO_PRO_CART', payload: value });

  // Handle size selection - transfer selected state from sizing kit to size button
  const handleSizeSelection = (selectedSize: RingSizes) => {
    setSize(selectedSize);
    setIsSizingKitSelected(false); // Move selection from sizing kit to size button
  };

  // Handle sizing kit card click - reset size selection and make sizing kit selected
  const handleSizingKitClick = () => {
    setSize(RingSizes.OPEN); // Reset to open size (sizing kit)
    setIsSizingKitSelected(true); // Make sizing kit selected again
    // Note: Size toggle visibility will be handled by the component's local state
  };

  const forwardShopifySearchParams = useForwardSearchParamsShopify();
  const eSizingSize = Boolean(searchParams?.get('eSizingSize'));

  const requiredFields = [
    { ref: showSizeRef, condition: !size },
    { ref: showChargerRef, condition: !chargerType },
    { ref: coverageRef, condition: uhx === false && uhxCart === null },
  ];

  const goToNextStep = () => {
    analytics.track('Bottom bar continue - CLICK');
    for (const { ref, condition } of requiredFields) {
      if (condition) {
        highlightElement(ref);
        break;
      }
    }
  };

  // Use dynamic shipping estimates from API if available, otherwise use SSR fallback
  // Return as span to avoid nested p tag issues when used in other components
  const shippingEstimate = useMemo(() => {
    // Try to get dynamic estimate from API
    const dynamicEstimate = getShippingEstimate({
      color,
      size,
      estimates,
      addonsState,
      t,
    });

    // If dynamic estimate is available, use it
    if (dynamicEstimate) {
      return dynamicEstimate;
    }

    // Fall back to SSR estimate or default message
    if (!rareShippingEstimate) {
      return (
        <span>
          Each ring is made to order, typically takes{' '}
          <span style={{ fontWeight: 500, color: '#000' }}>15-20 days</span>{' '}
          (after size selection)
        </span>
      );
    }

    return (
      <span>
        {rareShippingEstimate.message}{' '}
        <span style={{ fontWeight: 500, color: '#000' }}>
          {rareShippingEstimate.timeframe}
        </span>{' '}
        {rareShippingEstimate.subtitle}
      </span>
    );
  }, [color, size, estimates, addonsState, rareShippingEstimate]);

  const referral = searchParams?.get('referral')
    ? ringReferralCodes[store as ShopifyStore]
    : undefined;

  const workAffiliate =
    searchParams?.get('workAffiliate') && searchParams?.get('discount');
  const currencySymbol = CurrencySymbol[currency.toLowerCase() as Currency];

  const ultraHumanPrice = useMemo(
    () => (uhxCart && !uhx && store ? UhxShopifyMap[store][uhxCart].price : 0),
    [uhxCart, uhx, store],
  );
  const rarePriceObject =
    store && color
      ? RarePriceData[store as ShopifyStore]?.[color as RareColors] || {
          price: 0,
          striked_price: 0,
        }
      : { price: 0, striked_price: 0 };

  // Get region-specific discount configuration
  const discountConfigs = getRareDiscountConfig();
  const discountConfig = store ? discountConfigs[store as ShopifyStore] : null;
  const isDiscountEnabled = discountConfig?.enabled ?? false;
  const showStrikedPrice = discountConfig?.showStrikedPrice ?? false;

  // Calculate price drop based on region configuration
  const priceDrop = isDiscountEnabled
    ? rarePriceObject.striked_price - rarePriceObject.price
    : 0;

  const price = rarePriceObject.price;

  const chargerPrice = useMemo(
    () =>
      store && chargerType === RingChargerType.TRAVEL_CHARGER
        ? (getTravelChargerPrice(store) ?? 0)
        : 0,
    [store, chargerType],
  );

  const afibPrice = useMemo(
    () =>
      afibCart ? AfibShopifyMap[store as ShopifyStore][afibCart].price : 0,
    [afibCart, store],
  );
  const cardioAdaptibilityPrice = useMemo(
    () =>
      cardioAdaptibilityCart
        ? CardioAdaptibilityShopifyMap[store as ShopifyStore][
            cardioAdaptibilityCart
          ].price
        : 0,
    [cardioAdaptibilityCart, store],
  );
  const cnoProPrice = useMemo(
    () =>
      cnoProCart
        ? CnoProShopifyMap[store as ShopifyStore][cnoProCart].price
        : 0,
    [cnoProCart, store],
  );

  const affiliateDiscount = useMemo(() => {
    // Exclude affiliate discount when affiliateCode=birthday
    if (searchParams?.get('affiliateCode') === 'birthday') {
      return 0;
    }

    if (workAffiliate) {
      return (price * (workAffiliateDiscount?.discount ?? 0)) / 100;
    }
    const effectiveStore = getEffectiveStoreForDiscount(store);
    if (!effectiveStore) return 0;
    if (
      campaignActive ||
      !affiliateDetails ||
      !affiliateDetails.ringRareActive ||
      !affiliateDetails.discount_configs[effectiveStore]?.RING_RARE?.code ||
      searchParams?.get('referral')
    )
      return 0;
    const discountConfig =
      affiliateDetails.discount_configs[effectiveStore]?.RING_RARE;

    return discountConfig?.type === 'percentage'
      ? (discountConfig.value * price) / 100
      : discountConfig?.value ?? 0;
  }, [
    campaignActive,
    affiliateDetails,
    price,
    workAffiliate,
    workAffiliateDiscount,
    store,
    searchParams,
  ]);

  // --- Hero Discount ---
  const heroCategoryParam = searchParams?.get('heroCategory');
  const heroCategory =
    heroCategoryParam && heroDiscountCodes[heroCategoryParam]
      ? heroCategoryParam
      : null;
  const heroDiscount = heroCategory
    ? price * getHeroDiscountPercentage()
    : 0;

  // --- Birthday Discount ---
  // Ring uses only 'discount' parameter (Shopify sync)
  const birthdayDiscountCode = searchParams?.get('discount') as string;
  const affiliateCode = searchParams?.get('affiliateCode') as string;

  // Extract percentage from last 2 digits if they're numbers, otherwise default to 15%
  let birthdayDiscountPercentage = 0; // default
  if (birthdayDiscountCode && affiliateCode === 'birthday') {
    const lastTwoChars = birthdayDiscountCode.slice(-2);
    const parsedPercentage = parseInt(lastTwoChars);
    if (
      !isNaN(parsedPercentage) &&
      parsedPercentage > 0 &&
      parsedPercentage <= 100
    ) {
      birthdayDiscountPercentage = parsedPercentage;
    } else {
      birthdayDiscountPercentage = 15; // fallback to 15%
    }
  }
  const birthdayDiscount =
    birthdayDiscountCode &&
    birthdayDiscountCode.length > 0 &&
    affiliateCode === 'birthday'
      ? price * (birthdayDiscountPercentage / 100)
      : 0;

  const discounts = useMemo(
    () => [
      { type: 'referral', amount: referral ? price * 0.1 : 0 },
      { type: 'affiliate', amount: affiliateDiscount },
      { type: 'hero', amount: heroDiscount },
      { type: 'birthday', amount: birthdayDiscount },
    ],
    [referral, affiliateDiscount, heroDiscount, birthdayDiscount, price],
  );

  // CONFIG: uncomment bellow & comment above for disabling the discount.
  // const discounts = useMemo(
  //   () => [
  //     { type: 'referral', amount: 0 },
  //     { type: 'affiliate', amount: 0 },
  //     { type: 'hero', amount: 0 },
  //   ],
  //   [],
  // );

  const effectiveDiscount = useMemo(
    () =>
      discounts.reduce(
        (max, current) => (current.amount > max.amount ? current : max),
        { type: 'referral', amount: 0 },
      ),
    [discounts],
  );

  const effectivePrice = useMemo(
    () =>
      price +
      chargerPrice +
      cardioAdaptibilityPrice +
      afibPrice +
      cnoProPrice +
      ultraHumanPrice -
      effectiveDiscount.amount,
    [
      price,
      chargerPrice,
      cardioAdaptibilityPrice,
      afibPrice,
      cnoProPrice,
      ultraHumanPrice,
      effectiveDiscount,
    ],
  );

  // Addon pricing functions
  const getUhxCoveragePrice = useMemo(
    () => getRareAddonPricing(store as ShopifyStore, 'uhxCoverage'),
    [store],
  );

  const getLifetimeSoftwarePrice = useMemo(
    () => getRareAddonPricing(store as ShopifyStore, 'lifetimeSoftware'),
    [store],
  );

  // useEffect(() => {
  //   if (!color || !size) return;
  //   if (affiliateDiscount || referral) {
  //     setPrice(
  //       RingPriceStriked[getEngravingKey(engraving.enabled)][
  //         store as ShopifyStore
  //       ],
  //     );
  //     return;
  //   }
  //   setPrice(
  //     RingPrice[getEngravingKey(engraving.enabled)][
  //       store as ShopifyStore
  //     ],
  //   );
  // }, [
  //   engraving.enabled,
  //   affiliateDiscount,
  //   store,
  //   color,
  //   size,
  //   referral,
  // ]);

  useEffect(() => {
    if (searchParams?.get('affiliate') || searchParams?.get('affiliateCode')) {
      setUhxCart(false);
    }
  }, [searchParams]);

  const checkout = async () => {
    if (!color || !size || !store || notServicable === true) return;

    const productMap = productMap_rare;

    if (directCheckout) {
      if (
        misc &&
        ![null, undefined, ''].includes(productMap[color]?.[size]?.misc?.[misc])
      ) {
        const url = productMap[color]?.[size]?.misc?.[misc];
        const route = forwardShopifySearchParams(url as string, 'rare');
        await router.push(route);
        return;
      } else if (misc) {
        console.log('something went wrong');
        return;
      }
      if (productMap[color]?.[size]?.variantId[variantStore as ShopifyStore]) {
        const product =
          productMap[color]?.[size]?.variantId[variantStore as ShopifyStore];
        const route = forwardShopifySearchParams(product as string, 'rare');
        await router.push(route);
      } else {
        console.log('something went wrong!');
      }
      return;
    }
    try {
      setIsLoading(true);
      const lineItemsArray = getCheckoutLineItems({
        color,
        size,
        store: variantStore as ShopifyStore,
        chargerType,
        eSizingSize,
        uhxCart: uhxCart,
        afibCart: afibCart,
        cardioAdaptibilityCart: cardioAdaptibilityCart,
        cnoProCart: cnoProCart,
        misc,
      });
      console.log('RARE checkout line items:', lineItemsArray);
      console.log('RARE checkout params:', {
        color,
        size,
        store: variantStore,
        uhxCart,
        afibCart,
        cardioAdaptibilityCart,
        cnoProCart,
      });
      await addToCart(lineItemsArray, true);
    } catch (error) {
      alert('something went wrong!');
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <RareSelectionContext.Provider
      value={{
        color,
        setColor,
        size,
        setSize,
        chargerType,
        setChargerType,
        uhx,
        setUhx,
        uhxCart: uhxCart,
        setUhxCart,
        afibCart: afibCart,
        setAfibCart,
        cardioAdaptibilityCart: cardioAdaptibilityCart,
        setCardioAdaptabilityCart,
        cnoProCart: cnoProCart,
        setCnoProCart,
        checkout,
        isLoading,
        defaultCurrency: currency,
        currencySymbol,
        store,
        price,
        ultraHumanPrice,
        effectivePrice,
        chargerPrice,
        shippingEstimate,
        affiliateDetails,
        campaignActive,
        affiliateDiscount,
        referral,
        sectionRefs,
        goToNextStep,
        effectiveDiscount,
        priceDrop,
        isDiscountEnabled,
        showStrikedPrice,
        notServicable,
        notServicableMessage,
        isSizingKitSelected,
        setIsSizingKitSelected,
        handleSizeSelection,
        handleSizingKitClick,
        getUhxCoveragePrice,
        getLifetimeSoftwarePrice,
      }}
    >
      {children}
    </RareSelectionContext.Provider>
  );
};
